import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DataSourceKind } from '@bliks-io/contracts';
import type { UnsuppliedPerson } from './syncSelection';

const lowerFirst = (text: string) => text.charAt(0).toLocaleLowerCase() + text.slice(1);

/** Who the source cannot supply, one "Name · Grund" line each, with an optional action. */
export function UnsuppliedPeopleNote<TPerson extends UnsuppliedPerson>({
  people,
  source,
  action
}: {
  people: TPerson[];
  source: DataSourceKind;
  action?: (person: TPerson) => ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  if (people.length === 0) return null;
  return (
    <div className="mt-4 text-sm text-muted-foreground">
      <p id={id}>
        {t('dataSync.sync.unsuppliedPeople', {
          count: people.length,
          source: t(`dataSync.settings.kind.${source}`)
        })}
      </p>
      <ul aria-labelledby={id} className="mt-1.5 flex flex-col gap-0.5 text-xs">
        {people.map((person) => (
          <li key={person.key}>
            {person.name} ·{' '}
            {person.reasonKeys
              .map((key, index) => (index === 0 ? t(key) : lowerFirst(t(key))))
              .join(', ')}
            {action?.(person)}
          </li>
        ))}
      </ul>
    </div>
  );
}
